import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { apiRequest } from 'mastodon/api';
import type {
  ApiRoomChatAccessJSON,
  ApiRoomChatMessageJSON,
  ApiRoomParticipantJSON,
  ApiRoomPollJSON,
  ApiStreamEmoteJSON,
} from 'mastodon/api_types/rooms';
import { checkFileSize } from 'mastodon/constants/media_limits';
// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- untyped legacy container
import EmojiPickerDropdown from 'mastodon/features/compose/containers/emoji_picker_dropdown_container';

import { chatAccessPlaceholder, resolveChatAccess } from '../chat_access';
import { insertMention, matchParticipants, mentionQueryAt } from '../mentions';
import { parseVoteCommand } from '../polls';

import { ChatMessage, ChatSystemMessage } from './chat_message';
import { RoomPollPanel } from './room_poll_panel';
import { TopGiftersTicker } from './top_gifters_ticker';

interface PickedEmoji {
  native?: string;
  colons?: string;
  custom?: boolean;
}

interface Props {
  messages: ApiRoomChatMessageJSON[];
  // May return the request promise; when it does, slow-mode/timeout UI is
  // driven from the server's response.
  onSend: (
    text: string,
    mediaAttachmentId?: string,
    replyToId?: string,
  ) => unknown;
  /**
   * The people currently in the room. Drives @-tagging: the composer only
   * ever suggests accounts from this list, and only handles from this list
   * render as tags. Optional — a chat without it simply has no tagging.
   */
  participants?: ApiRoomParticipantJSON[];
  compact?: boolean;
  /** The room this chat belongs to. Required for the poll dock. */
  roomId?: string;
  /**
   * The room poll, if one is up. Drives typed voting (`/vote 2`, or a bare `2`
   * before they have voted) AND the poll docked above the message list.
   * Optional: a chat without it simply has no poll and no typed voting.
   */
  poll?: ApiRoomPollJSON | null;
  onVote?: (optionIndex: number) => unknown;
  /** Viewer is the host or a co-host: enables moderation UI. */
  canModerate?: boolean;
  /** The room host's account id — the host's messages are off-limits to co-hosts. */
  hostAccountId?: string;
  /**
   * Speaker/host-only privilege (server-enforced): listeners get a text-only
   * composer — no attach button. Defaults to false.
   */
  canPostMedia?: boolean;
  /** The room host's custom emotes: rendered in messages + pickable. */
  emotes?: ApiStreamEmoteJSON[];
  currentAccountId?: string;
  /** Seconds between messages per non-host account (0/undefined = off). */
  slowModeSeconds?: number;
  /**
   * Ticket 72 - this viewer's followers-only verdict from the server. Absent
   * means no gate, which is what every room does unless its host turned the
   * switch on.
   */
  chatAccess?: ApiRoomChatAccessJSON | null;
  /**
   * Epoch ms at which a follow made in THIS session opens chat. Set on the
   * click so the countdown starts immediately rather than up to 3s later when
   * the room poll catches up.
   */
  chatUnlockAt?: number | null;
  pinnedMessage?: ApiRoomChatMessageJSON | null;
  onDeleteMessage?: (messageId: string) => void;
  onPinMessage?: (messageId: string) => void;
  onUnpinMessage?: (messageId: string) => void;
  onTimeoutUser?: (accountId: string, durationSeconds: number) => void;
  /**
   * Ticket 51: live viewer count, shown in the chat title. Optional for
   * forward compatibility — a caller that does not know the count renders the
   * title exactly as before rather than a zero it cannot vouch for.
   */
  viewerCount?: number;
  /** Host-only channel ban — see ChatMessage's `onBan`. */
  onBanUser?: (accountId: string, displayName: string) => void;
  onSetSlowMode?: (seconds: number) => void;
  /**
   * Ticket 75: the room host's display name, for the top-gifters board (it
   * names whose board it is). Optional — without it the board falls back to
   * "the host" rather than not rendering.
   */
  hostName?: string;
  /**
   * Ticket 75: whether gifting is switched on for this instance. Decides
   * whether the roller appears at all when nobody has gifted yet, and whether
   * the board offers a Gift Sub button.
   */
  giftSubsEnabled?: boolean;
}

// Twitch-style chat cap, mirrored by the server (RoomChatMessage validates
// length <= 500 and 422s past it). Exported for tests.
export const CHAT_MAX_LENGTH = 500;

const SLOW_MODE_CHOICES: { label: string; seconds: number }[] = [
  { label: 'Off', seconds: 0 },
  { label: '5s', seconds: 5 },
  { label: '10s', seconds: 10 },
  { label: '30s', seconds: 30 },
  { label: '1m', seconds: 60 },
  { label: '5m', seconds: 300 },
];

// Axios error shape for the two send-rejection cases we surface in the UI.
interface SendRejection {
  status?: number;
  retryAfter?: number;
  timeoutExpiresAt?: string;
}

const isThenable = (v: unknown): v is Promise<unknown> =>
  typeof v === 'object' &&
  v !== null &&
  typeof (v as { then?: unknown }).then === 'function';

const parseSendError = (err: unknown): SendRejection => {
  const response = (
    err as {
      response?: {
        status?: number;
        data?: { retry_after?: number; timeout_expires_at?: string };
      };
    }
  ).response;
  return {
    status: response?.status,
    retryAfter: response?.data?.retry_after,
    timeoutExpiresAt: response?.data?.timeout_expires_at,
  };
};

/**
 * Ticket 51: viewer counts render short so a five-figure audience cannot push
 * the poll and slow-mode controls out of the chat header. Under a thousand is
 * exact, because the difference between 6 and 7 viewers matters to a streamer
 * in a way the difference between 12,400 and 12,500 does not.
 */
export const formatViewerCount = (count: number): string => {
  if (!Number.isFinite(count) || count < 0) return '0';
  const n = Math.floor(count);
  if (n < 1000) return String(n);
  if (n < 1_000_000) {
    const k = n / 1000;
    return `${k < 10 ? k.toFixed(1).replace(/\.0$/, '') : Math.floor(k)}K`;
  }
  const m = n / 1_000_000;
  return `${m < 10 ? m.toFixed(1).replace(/\.0$/, '') : Math.floor(m)}M`;
};

export const RoomChat: React.FC<Props> = ({
  messages,
  onSend,
  participants = [],
  compact,
  roomId,
  poll = null,
  onVote,
  canModerate,
  hostAccountId,
  canPostMedia = false,
  emotes = [],
  currentAccountId,
  slowModeSeconds = 0,
  chatAccess,
  chatUnlockAt,
  pinnedMessage,
  onDeleteMessage,
  onPinMessage,
  onUnpinMessage,
  onTimeoutUser,
  onBanUser,
  onSetSlowMode,
  hostName,
  giftSubsEnabled,
  viewerCount,
}) => {
  const [text, setText] = useState('');
  // Ticket 29: the poll's create form is opened from the chat header and drawn
  // by the dock above the message list, so the one boolean linking them lives
  // here. All the poll's own state stays inside RoomPollPanel.
  const [pollFormOpen, setPollFormOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadWarning, setUploadWarning] = useState<string | null>(null);
  // Why the last send did not go through, when it is not slow mode or a timeout.
  const [sendWarning, setSendWarning] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ url: string; id: string } | null>(
    null,
  );
  // Epoch ms until which sending is blocked, and why. Covers both the local
  // slow-mode clock and server-reported rejections (429 slow mode, 403 timeout).
  const [blockedUntil, setBlockedUntil] = useState<number | null>(null);
  const [blockReason, setBlockReason] = useState<'slow' | 'timeout' | null>(
    null,
  );
  const [now, setNow] = useState(() => Date.now());
  // The message being answered, if any. Cleared on send, on cancel and on Esc.
  const [replyTo, setReplyTo] = useState<ApiRoomChatMessageJSON | null>(null);
  // @-tagging: the query under the caret and which suggestion is highlighted.
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);

  const handlePollFormToggle = useCallback(() => {
    setPollFormOpen((v) => !v);
  }, []);

  const handlePollFormClose = useCallback(() => {
    setPollFormOpen(false);
  }, []);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (
      messagesEndRef.current &&
      typeof messagesEndRef.current.scrollIntoView === 'function'
    ) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length]);

  // Tick the countdown while blocked.
  useEffect(() => {
    // Also ticks while a chat-access countdown is running: that clock is the
    // whole of ticket 72, and it has to move without a message being sent.
    const counting =
      (chatAccess && !chatAccess.can_chat) ||
      (typeof chatUnlockAt === 'number' && chatUnlockAt > Date.now());
    if (blockedUntil === null && !counting) return undefined;
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 500);
    return () => {
      clearInterval(interval);
    };
  }, [blockedUntil, chatAccess, chatUnlockAt]);

  const secondsLeft =
    blockedUntil !== null ? Math.ceil((blockedUntil - now) / 1000) : 0;
  // Ticket 72. Kept separate from the slow-mode/timeout clock above: those are
  // "wait your turn", this is "you are not allowed in yet", and the composer
  // says different things for each. `isBlocked` is the union, so every existing
  // guard keeps working without having to know about the new gate.
  // Ticket 75: the top-gifters board goes live off the chat poll rather than a
  // poll of its own. A completed gift writes a `gifted_subs` line into chat, so
  // counting those lines gives the board a token that changes exactly when a
  // new gift has landed and at no other time.
  const giftEventCount = useMemo(
    () => messages.filter((msg) => msg.event === 'gifted_subs').length,
    [messages],
  );

  const access = resolveChatAccess(chatAccess, now, chatUnlockAt);
  const isBlocked = secondsLeft > 0 || !access.canChat;

  useEffect(() => {
    if (blockedUntil !== null && secondsLeft <= 0) {
      setBlockedUntil(null);
      setBlockReason(null);
    }
  }, [blockedUntil, secondsLeft]);

  const handleSend = useCallback(() => {
    const trimmed = text.trim();
    if ((!trimmed && !preview) || isBlocked) return;
    // The input clamps typing, but programmatic inserts could sneak past —
    // the server rejects >500 with a 422, so refuse client-side too.
    if (trimmed.length > CHAT_MAX_LENGTH) return;

    // Voting by typing (ticket 29). A vote is not a message: it is cast and the
    // input clears, with nothing posted to chat. Only when there is no attached
    // media — "/vote 1" with a picture attached is someone posting a picture.
    if (onVote && !preview) {
      const command = parseVoteCommand(trimmed, poll);

      if (command.kind === 'vote') {
        setSendWarning(null);
        setText('');
        setMentionQuery(null);
        onVote(command.optionIndex);
        return;
      }

      if (command.kind === 'error') {
        // Keep what they typed: an explicit /vote that could not be honoured
        // should say why, not vanish.
        setSendWarning(command.message);
        return;
      }
    }

    const previousPreview = preview;
    const previousReplyTo = replyTo;
    setSendWarning(null);
    const result = onSend(trimmed, preview?.id, replyTo?.id);
    setText('');
    setPreview(null);
    setReplyTo(null);
    setMentionQuery(null);

    if (isThenable(result)) {
      result
        .then(() => {
          // Start the local slow-mode clock so the user sees the wait
          // immediately instead of discovering it via a 429.
          if (!canModerate && slowModeSeconds > 0) {
            setBlockedUntil(Date.now() + slowModeSeconds * 1000);
            setBlockReason('slow');
          }
          return undefined;
        })
        .catch((err: unknown) => {
          const rejection = parseSendError(err);
          if (rejection.status === 429 && rejection.retryAfter) {
            setBlockedUntil(Date.now() + rejection.retryAfter * 1000);
            setBlockReason('slow');
          } else if (rejection.status === 403 && rejection.timeoutExpiresAt) {
            setBlockedUntil(new Date(rejection.timeoutExpiresAt).getTime());
            setBlockReason('timeout');
          }
          // Don't lose what they typed — including who they were answering.
          setText(trimmed);
          setPreview(previousPreview);
          if (rejection.status === 404 && previousReplyTo) {
            // The message was deleted between pressing reply and sending.
            // Drop the quote and say so: silently sending it as an ordinary
            // message, or silently failing, both read as the app eating it.
            setSendWarning(
              'The message you were replying to was deleted. Your message was not sent — send it again to post it on its own.',
            );
          } else {
            setReplyTo(previousReplyTo);
          }
        });
    }
  }, [
    text,
    preview,
    replyTo,
    onSend,
    isBlocked,
    canModerate,
    slowModeSeconds,
    poll,
    onVote,
  ]);

  const suggestions = useMemo(
    () =>
      mentionQuery === null
        ? []
        : matchParticipants(participants, mentionQuery, currentAccountId),
    [mentionQuery, participants, currentAccountId],
  );
  const suggestionsOpen = suggestions.length > 0;

  const refreshMentionQuery = useCallback((value: string, caret: number) => {
    const found = mentionQueryAt(value, caret);
    setMentionQuery(found ? found.query : null);
    setMentionIndex(0);
  }, []);

  const applyMention = useCallback(
    (handle: string) => {
      const input = textInputRef.current;
      const caret = input?.selectionStart ?? text.length;
      const next = insertMention(text, caret, handle);
      if (next.text.length > CHAT_MAX_LENGTH) return;
      setText(next.text);
      setMentionQuery(null);
      // Put the caret back after the handle rather than at the end: people
      // tag mid-sentence ("thanks @alice for the...").
      requestAnimationFrame(() => {
        input?.focus();
        input?.setSelectionRange(next.caret, next.caret);
      });
    },
    [text],
  );

  const handleSuggestionClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const handle = e.currentTarget.dataset.handle;
      if (handle) applyMention(handle);
    },
    [applyMention],
  );

  const handleCancelReply = useCallback(() => {
    setReplyTo(null);
    textInputRef.current?.focus();
  }, []);

  const handleReplyTo = useCallback((message: ApiRoomChatMessageJSON) => {
    setReplyTo(message);
    textInputRef.current?.focus();
  }, []);

  const handleJumpToMessage = useCallback((messageId: string) => {
    const el = document.getElementById(`room-chat-message-${messageId}`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('room-chat__message--flash');
      setTimeout(() => {
        el.classList.remove('room-chat__message--flash');
      }, 1200);
    }
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // While the tag picker is open it owns the arrow keys, Enter and Tab —
      // otherwise Enter would send "@ali" instead of picking @alice.
      if (suggestionsOpen) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setMentionIndex((i) => (i + 1) % suggestions.length);
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setMentionIndex(
            (i) => (i - 1 + suggestions.length) % suggestions.length,
          );
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          const chosen = suggestions[mentionIndex] ?? suggestions[0];
          if (chosen) applyMention(chosen.account.acct);
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setMentionQuery(null);
          return;
        }
      }

      if (e.key === 'Escape' && replyTo) {
        e.preventDefault();
        setReplyTo(null);
        return;
      }

      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [
      handleSend,
      suggestionsOpen,
      suggestions,
      mentionIndex,
      applyMention,
      replyTo,
    ],
  );

  const uploadAbortRef = useRef<AbortController | null>(null);

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      // Reset immediately so re-selecting the same file re-fires onChange.
      if (fileInputRef.current) fileInputRef.current.value = '';

      // The limit message promises "it will not be uploaded" — honor that.
      // Reject outright instead of warning while uploading anyway.
      const sizeWarning = checkFileSize(file);
      setUploadWarning(sizeWarning);
      if (sizeWarning) return;

      const controller = new AbortController();
      uploadAbortRef.current = controller;
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const resp = await apiRequest<{
          id: string;
          url: string;
          preview_url: string;
        }>('POST', 'v1/media', {
          data: formData as unknown as Record<string, unknown>,
          signal: controller.signal,
        });
        setPreview({ url: resp.preview_url || resp.url, id: resp.id });
      } catch {
        // Silence here was the bug: the user watched "..." forever with no
        // outcome. A cancel is intentional — anything else gets a message.
        if (!controller.signal.aborted) {
          setUploadWarning(
            'Upload failed — check your connection and try again.',
          );
        }
      }
      uploadAbortRef.current = null;
      setUploading(false);
    },
    [],
  );

  const handleCancelUpload = useCallback(() => {
    uploadAbortRef.current?.abort();
  }, []);

  const handleRemovePreview = useCallback(() => {
    setPreview(null);
  }, []);

  const [emotesOpen, setEmotesOpen] = useState(false);

  const handleEmotesToggle = useCallback(() => {
    setEmotesOpen((v) => !v);
  }, []);

  const handleEmoteInsert = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const code = e.currentTarget.dataset.code;
      if (!code) return;
      setEmotesOpen(false);
      setText((t) => {
        const next = `${t}${t.length > 0 && !t.endsWith(' ') ? ' ' : ''}:${code}: `;
        // Refuse rather than truncate: a sliced ":cod" would post garbage.
        return next.length > CHAT_MAX_LENGTH ? t : next;
      });
      textInputRef.current?.focus();
    },
    [],
  );

  const handleEmojiPick = useCallback(
    (data: PickedEmoji) => {
      // Unicode emojis insert their character; custom emojis insert their
      // :shortcode: (which needs surrounding whitespace to be parsed).
      const emojiStr = data.native ?? data.colons ?? '';
      if (!emojiStr) return;
      const input = textInputRef.current;
      const pos = input?.selectionStart ?? text.length;
      const before = text.slice(0, pos);
      const after = text.slice(pos);
      const pad = data.custom && before.length > 0 && !before.endsWith(' ');
      const next = `${before}${pad ? ' ' : ''}${emojiStr} ${after}`;
      if (next.length <= CHAT_MAX_LENGTH) setText(next);
      input?.focus();
    },
    [text],
  );

  const handleSlowModeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onSetSlowMode?.(Number(e.target.value));
    },
    [onSetSlowMode],
  );

  const handleUnpin = useCallback(() => {
    if (pinnedMessage) onUnpinMessage?.(pinnedMessage.id);
  }, [onUnpinMessage, pinnedMessage]);

  // Only accounts actually in the room render as tags, and only they can be
  // suggested. Both acct and bare username so @alice and @alice@host match.
  const knownHandles = new Set(
    participants.flatMap((p) => [
      p.account.acct.toLowerCase(),
      p.account.username.toLowerCase(),
    ]),
  );
  const viewer = participants.find((p) => p.account.id === currentAccountId);

  const placeholder = !access.canChat
    ? chatAccessPlaceholder(access)
    : !isBlocked
      ? 'Send a message...'
      : blockReason === 'timeout'
        ? `You are timed out (${secondsLeft}s)`
        : `Slow mode: wait ${secondsLeft}s`;

  return (
    <div className={`room-chat ${compact ? 'room-chat--compact' : ''}`}>
      <div className='room-chat__header'>
        <span>Chat</span>
        {/* Ticket 51: viewers, between the title and the poll button. Counts
            are formatted short — a raid can put five figures in a header that
            is only a few hundred pixels wide, and "12.4K" is both readable and
            a fixed width where "12437" pushes the controls beside it around. */}
        {typeof viewerCount === 'number' && viewerCount >= 0 && (
          <span
            className='room-chat__viewer-count'
            data-testid='chat-viewer-count'
            title={`${viewerCount} ${viewerCount === 1 ? 'person' : 'people'} in this room`}
          >
            <span aria-hidden='true'>👁</span> {formatViewerCount(viewerCount)}
          </span>
        )}
        {/* Ticket 29: the poll control lives here rather than on the
            jumbotron. The jumbotron is a full-width row above the video stage
            and the stage is the row that absorbs its growth, so anything tall
            landing there resizes the stream. */}
        {canModerate && roomId && !poll && (
          <button
            type='button'
            className='room-chat__poll-btn'
            onClick={handlePollFormToggle}
            aria-expanded={pollFormOpen}
            data-testid='jumbotron-poll-btn'
          >
            {pollFormOpen ? 'Cancel' : '📊 Poll'}
          </button>
        )}
        {canModerate && onSetSlowMode ? (
          <label className='room-chat__slow-mode-control'>
            🐢
            <select
              value={
                SLOW_MODE_CHOICES.some((c) => c.seconds === slowModeSeconds)
                  ? slowModeSeconds
                  : 0
              }
              onChange={handleSlowModeChange}
              aria-label='Slow mode'
            >
              {SLOW_MODE_CHOICES.map((choice) => (
                <option key={choice.seconds} value={choice.seconds}>
                  {choice.seconds === 0
                    ? 'Slow mode off'
                    : `Slow ${choice.label}`}
                </option>
              ))}
            </select>
          </label>
        ) : (
          slowModeSeconds > 0 && (
            <span
              className='room-chat__slow-mode-badge'
              title='Slow mode is on'
            >
              🐢 {slowModeSeconds}s
            </span>
          )
        )}
      </div>
      {/*
        Ticket 75. Docked directly under the header, which is where the
        operator's screenshot puts it. The board it opens is drawn OVER the
        messages rather than in the flow: it is tall (a podium plus up to fifty
        rows), and docking something that size in flow would leave the message
        list a few pixels high for as long as it was open. It is opened
        deliberately and closes on click-outside, Escape or a second click,
        which is what makes covering the messages acceptable here and was not
        the case for ticket 29's unbidden panel.
      */}
      {roomId && (
        <TopGiftersTicker
          roomId={roomId}
          hostName={hostName ?? 'the host'}
          giftSubsEnabled={giftSubsEnabled}
          refreshToken={giftEventCount}
        />
      )}
      {pinnedMessage && !pinnedMessage.deleted && (
        <div className='room-chat__pinned'>
          <span className='room-chat__pinned-icon'>📌</span>
          <span className='room-chat__pinned-body'>
            <span className='room-chat__pinned-name'>
              {pinnedMessage.account.display_name ||
                pinnedMessage.account.username}
            </span>{' '}
            {pinnedMessage.content}
          </span>
          {canModerate && onUnpinMessage && (
            <button
              type='button'
              className='room-chat__pinned-unpin'
              onClick={handleUnpin}
              aria-label='Unpin message'
              title='Unpin'
            >
              &times;
            </button>
          )}
        </div>
      )}
      {/*
        The poll docks BETWEEN the header and the message list, in the chat's
        own flow — it is not floated over the messages. An opaque card pinned
        over a scrolling list hides whatever passes under it, which is exactly
        what ticket 29's `chathides.jpeg` reported. Occupying real space here
        means no message is ever underneath it.

        This cannot resize the video: `.room-chat` carries `min-height: 0` in
        both layouts, so its own box is content-independent and a child inside
        it takes height from the message list rather than from the stage.
      */}
      {roomId && (
        <RoomPollPanel
          roomId={roomId}
          poll={poll ?? null}
          canManage={canModerate ?? false}
          formOpen={pollFormOpen}
          onCloseForm={handlePollFormClose}
        />
      )}
      <div className='room-chat__messages'>
        {messages.map((msg) =>
          // A server-written announcement (ticket 69) is not somebody's
          // message: it renders as a plain line with none of the author or
          // moderation furniture around it.
          msg.event ? (
            <ChatSystemMessage key={msg.id} message={msg} />
          ) : (
            <ChatMessage
              key={msg.id}
              message={msg}
              canModerate={canModerate}
              hostAccountId={hostAccountId}
              currentAccountId={currentAccountId}
              emotes={emotes}
              knownHandles={knownHandles}
              currentAccountAcct={viewer?.account.acct}
              currentAccountUsername={viewer?.account.username}
              onDelete={onDeleteMessage}
              onPin={onPinMessage}
              onTimeout={onTimeoutUser}
              onBan={onBanUser}
              onReply={handleReplyTo}
              onJumpToMessage={handleJumpToMessage}
            />
          ),
        )}
        <div ref={messagesEndRef} />
      </div>
      {replyTo && (
        <div className='room-chat__replying' data-testid='chat-replying-bar'>
          <span className='room-chat__replying-body'>
            <span className='room-chat__replying-label'>
              Replying to{' '}
              <strong>
                {replyTo.account.display_name || replyTo.account.username}
              </strong>
            </span>
            <span className='room-chat__replying-text'>
              {replyTo.content || 'Attachment'}
            </span>
          </span>
          <button
            type='button'
            className='room-chat__replying-cancel'
            onClick={handleCancelReply}
            aria-label='Cancel reply'
            title='Cancel reply'
          >
            &times;
          </button>
        </div>
      )}
      {preview && (
        <div className='room-chat__preview'>
          <img
            src={preview.url}
            alt='Upload preview'
            className='room-chat__preview-image'
          />
          <button
            className='room-chat__preview-remove'
            onClick={handleRemovePreview}
            aria-label='Remove'
          >
            &times;
          </button>
        </div>
      )}
      {suggestionsOpen && (
        <div
          className='room-chat__mention-suggestions'
          role='listbox'
          aria-label='Tag someone in this room'
          data-testid='chat-mention-suggestions'
        >
          {suggestions.map((participant, i) => (
            <button
              key={participant.account.id}
              type='button'
              role='option'
              aria-selected={i === mentionIndex}
              className={`room-chat__mention-suggestion${i === mentionIndex ? ' room-chat__mention-suggestion--active' : ''}`}
              data-handle={participant.account.acct}
              onClick={handleSuggestionClick}
            >
              <img src={participant.account.avatar} alt='' aria-hidden='true' />
              <span className='room-chat__mention-suggestion-name'>
                {participant.account.display_name ||
                  participant.account.username}
              </span>
              <span className='room-chat__mention-suggestion-handle'>
                @{participant.account.acct}
              </span>
              {participant.role !== 'listener' && (
                <span className='room-chat__mention-suggestion-role'>
                  {participant.role === 'cohost' ? 'co-host' : participant.role}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
      {!access.canChat && (
        <div
          className={`room-chat__access-notice${access.counting ? ' room-chat__access-notice--counting' : ''}`}
          data-testid='chat-access-notice'
          role='status'
          aria-live='polite'
        >
          {access.counting ? (
            <>
              <span className='room-chat__access-notice__count'>
                {access.secondsLeft}
              </span>
              <span>
                Chat unlocks in {access.secondsLeft}{' '}
                {access.secondsLeft === 1 ? 'second' : 'seconds'}
              </span>
            </>
          ) : (
            <span>
              {access.gate === 'followers_only'
                ? 'Follow the host to chat'
                : (access.message ?? 'You cannot post in this chat yet')}
            </span>
          )}
        </div>
      )}
      <div className='room-chat__input'>
        {canPostMedia && (
          <>
            <button
              className='room-chat__upload-btn'
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || isBlocked}
              title='Attach media'
              aria-label='Attach media'
            >
              {uploading ? '...' : '📎'}
            </button>
            {uploading && (
              <button
                type='button'
                className='room-chat__upload-cancel'
                onClick={handleCancelUpload}
                title='Cancel upload'
                aria-label='Cancel upload'
              >
                ✕
              </button>
            )}
            <input
              ref={fileInputRef}
              type='file'
              accept='image/*,video/*,audio/*'
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
          </>
        )}
        <span className='room-chat__emoji-btn'>
          <EmojiPickerDropdown onPickEmoji={handleEmojiPick} />
        </span>
        {emotes.length > 0 && (
          <span className='room-chat__emotes'>
            <button
              type='button'
              className='room-chat__emotes-btn'
              onClick={handleEmotesToggle}
              aria-expanded={emotesOpen}
              title='Channel emotes'
              aria-label='Channel emotes'
            >
              <img src={emotes[0]?.url} alt='' aria-hidden='true' />
            </button>
            {emotesOpen && (
              <div className='room-chat__emotes-pop' role='menu'>
                {emotes.map((emote) => (
                  <button
                    key={emote.id}
                    type='button'
                    data-code={emote.shortcode}
                    onClick={handleEmoteInsert}
                    title={`:${emote.shortcode}:`}
                    aria-label={`Insert :${emote.shortcode}:`}
                  >
                    <img src={emote.url} alt={`:${emote.shortcode}:`} />
                  </button>
                ))}
              </div>
            )}
          </span>
        )}
        <input
          ref={textInputRef}
          type='text'
          placeholder={placeholder}
          value={text}
          maxLength={CHAT_MAX_LENGTH}
          onChange={(e) => {
            // maxLength covers typing; slice covers paste paths some
            // browsers let through.
            const next = e.target.value.slice(0, CHAT_MAX_LENGTH);
            setText(next);
            refreshMentionQuery(next, e.target.selectionStart ?? next.length);
          }}
          onKeyDown={handleKeyDown}
          disabled={(isBlocked && blockReason === 'timeout') || !access.canChat}
          data-testid='chat-text-input'
        />
        {text.length >= CHAT_MAX_LENGTH - 50 && (
          <span
            className='room-chat__charcount'
            data-testid='chat-charcount'
            aria-live='polite'
          >
            {CHAT_MAX_LENGTH - text.length}
          </span>
        )}
        <button
          onClick={handleSend}
          disabled={isBlocked}
          aria-label='Send chat message'
        >
          {!access.canChat
            ? access.counting
              ? `${access.secondsLeft}s`
              : '🔒'
            : isBlocked
              ? `${secondsLeft}s`
              : 'Send'}
        </button>
      </div>
      {uploadWarning && (
        <div className='dm-compose__warning'>{uploadWarning}</div>
      )}
      {sendWarning && (
        <div className='dm-compose__warning' data-testid='chat-send-warning'>
          {sendWarning}
        </div>
      )}
    </div>
  );
};
